// =====================================================================
// src/lib/financeiro.js — a conta do Financeiro do dono, sem DOM
//
// Cópia de `_calcResultadoHolding` (MdM, src/lib/calc/resultado-holding.js)
// e da cabeça de `renderFinanceiro` (index.html 11496): o faturamento
// bruto pro-rata por noite, o quadro azul "Resultado" com receitas,
// despesas (condomínio lançado × previsto, funcionários, IPTU, variáveis,
// outras), a loja como centro de custo à parte, e os dois líquidos.
//
// A ÚNICA divergência de fundo, decidida pelo Hugo em 21/09/2026 (Opção A):
// no MdM a regra de repasse está escrita com nome de gente —
//   if (p === 'Monteiro de Moraes') 100% ; 'FerTat' → 2.800 × meses ; else 25%
// — aqui ela é DADO: `acordos_da_imobiliaria` (0049), uma linha por
// proprietário (`proprio` | `cota_fixa` | `percentual`) e uma regra padrão
// (proprietario_id nulo) para quem não tem acordo próprio. A conta é a
// mesma; quem muda é onde mora a regra. Os acordos da Monteiro entram por
// seed, só para ela.
//
// O que ficou de FORA nesta rodada, e por quê:
//   · aluguel de berço: extra do item 6 ainda a definir. O frigobar entrou
//     em 22/09 (0056): lucro = receita − custo − comissão% da imobiliária
//     (`imobiliarias.comissao_frigobar`, 2A), somado à receita da holding
//     como no MdM (resultado-holding.js 122–156). O custo sai dos movimentos
//     × valor de compra ATUAL quando a tela os traz; senão, dos lançamentos.
//   · nota fiscal da despesa: segunda rodada (Hugo, 21/09).
//   · "Importar Backup" e "Zerar Dados": eram do e-mail admin do MdM.
//
// Tipos, como no MdM: `ano` e `mes` são STRINGS ('2026', '09'), e o
// período é comparado como ISO. Datas: `check_in`/`check_out`/`data`
// em 'YYYY-MM-DD'. Valores de reserva vêm por `valorDe(r)` (a função
// `valores_das_reservas`, 0015) — nulo = sem valor lançado = 0.
// =====================================================================
import { noites } from './reservas.js';
import { dadosDoFrigobar, lucroDoFrigobar } from './frigobar.js';

export const CATEGORIAS = Object.freeze([
  // `catLabel` (17838) + o select de `modalNovoLancamento` (11778)
  { chave: 'diarias', rotulo: 'Diárias' },
  { chave: 'condominio', rotulo: 'Condomínio' },
  { chave: 'manutencao', rotulo: 'Manutenção' },
  { chave: 'funcionarios', rotulo: 'Funcionários' },
  { chave: 'loja', rotulo: 'Loja' },
  { chave: 'salario', rotulo: 'Salário (legado)' },
  { chave: 'frigobar', rotulo: 'Frigobar' },
  { chave: 'aluguel_berco', rotulo: 'Aluguel Berço' },
  { chave: 'iptu', rotulo: 'IPTU' },
  { chave: 'imposto', rotulo: 'Imposto' },
  { chave: 'outros', rotulo: 'Outros' },
]);
export function rotuloDaCategoria(c) { return (CATEGORIAS.find(x => x.chave === c) || {}).rotulo || c || '-'; }

// `_despOpTipos` do MdM (11866) — a lista padrão; o tipo é texto livre.
export const TIPOS_DE_DESPESA = Object.freeze(['Manutenção Elétrica', 'Manutenção Hidráulica', 'Pintura', 'Móveis',
  'Eletrodomésticos', 'Limpeza Pesada', 'Material de Construção', 'Reparos Gerais', 'Outros']);

export const MESES = Object.freeze(['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']);

export const TIPOS_DE_ACORDO = Object.freeze([
  { chave: 'proprio', rotulo: 'Próprio (100% da imobiliária)' },
  { chave: 'cota_fixa', rotulo: 'Cota fixa por mês' },
  { chave: 'percentual', rotulo: 'Percentual do faturamento' },
]);

// ── período ──────────────────────────────────────────────────────────
// `renderFinanceiro`: ano+mês → o mês; só ano → o ano; nada → tudo.
export function periodoDe(ano, mes) {
  if (ano && mes) {
    const u = new Date(Number(ano), Number(mes), 0).getDate();
    return { ini: `${ano}-${mes}-01`, fim: `${ano}-${mes}-${String(u).padStart(2, '0')}` };
  }
  if (ano) return { ini: `${ano}-01-01`, fim: `${ano}-12-31` };
  return { ini: null, fim: null };
}
// Filtro padrão ao abrir a tela: o mês corrente (`_FIN_FRESH_NAV`).
export function filtroPadrao(hoje) { return { ano: hoje.slice(0, 4), mes: hoje.slice(5, 7) }; }
// Anos do select: os que têm lançamento + o atual + 5 à frente, decrescente.
export function anosDisponiveis(lancamentos, hoje) {
  const s = new Set((lancamentos || []).map(l => l.data && l.data.slice(0, 4)).filter(Boolean));
  const a = Number(hoje.slice(0, 4));
  for (let i = 0; i <= 5; i++) s.add(String(a + i));
  return [...s].sort().reverse();
}
export function rotuloDoFiltro(ano, mes) { return ano || mes ? ` (${mes ? mes + '/' : ''}${ano})` : ''; }

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const somar = arr => arr.reduce((s, l) => s + num(l.valor), 0);
function noPeriodo(d, { ini, fim }) {
  if (!d) return false;
  if (ini && d < ini) return false;
  if (fim && d > fim) return false;
  return true;
}

// ── receita pro-rata por noite (regra do Hugo, 30/04/2026 no MdM) ────
// A fração das noites da reserva que caem dentro do período, × o valor.
// Sem período: a reserva inteira. Cancelada não conta (no-show conta —
// o valor é devido, é o que o MdM faz).
export function receitaNoPeriodo(r, periodo, valorDe) {
  if (r.situacao === 'cancelada' || !r.check_in || !r.check_out) return 0;
  const v = num(valorDe(r));
  if (!periodo.ini || !periodo.fim) return v;
  const total = Math.max(1, noites(r.check_in, r.check_out));
  const ini = r.check_in > periodo.ini ? r.check_in : periodo.ini;
  // fim exclusivo: o dia seguinte ao último do período
  const fimEx = somarDiaISO(periodo.fim, 1);
  const fim = r.check_out < fimEx ? r.check_out : fimEx;
  if (ini >= fim) return 0;
  const dentro = noites(ini, fim);
  if (dentro <= 0) return 0;
  return v * (dentro / total);
}
function somarDiaISO(iso, n) { const [a, m, d] = iso.split('-').map(Number); const x = new Date(Date.UTC(a, m - 1, d + n)); return x.toISOString().slice(0, 10); }

// ── acordos ──────────────────────────────────────────────────────────
// O acordo que vale para um proprietário: o dele, senão a regra padrão
// (proprietario_id nulo), senão nenhum — e "nenhum" é avisado na tela, não
// virado em zero em silêncio.
export function acordoDe(acordos, proprietarioId) {
  const lista = acordos || [];
  return lista.find(a => a.proprietario_id && a.proprietario_id === proprietarioId)
    || lista.find(a => !a.proprietario_id)
    || null;
}
export function rotuloDoAcordo(a, moeda = v => String(v)) {
  if (!a) return 'sem acordo';
  if (a.tipo === 'proprio') return '100%';
  if (a.tipo === 'cota_fixa') return `${moeda(a.valor)} / mês`;
  return `${num(a.valor)}%`;
}
// O que a imobiliária fica de um bruto sob este acordo.
export function repasse(a, bruto, meses) {
  if (!a) return 0;
  if (a.tipo === 'proprio') return bruto;
  if (a.tipo === 'cota_fixa') return num(a.valor) * meses;
  return bruto * (num(a.valor) / 100);
}

// ── `_iptuPrevFlat` (custo-operacional.js): as parcelas do IPTU cadastrado que caem no período ─
// iptu = { valor_anual, parcelas, mes_inicio } (0053). O ano das parcelas
// é o do `de`; parcela passando de dezembro vira o ano seguinte (o MdM
// faz o mesmo e chama de "simplificação").
export function iptuPrevisto(iptu, de, ate) {
  if (!iptu || !(num(iptu.valor_anual) > 0) || !(parseInt(iptu.parcelas, 10) > 0) || !(parseInt(iptu.mes_inicio, 10) >= 1)) return 0;
  if (!de || !ate) return 0;
  const anoIni = Number(de.slice(0, 4)), mesIni = Number(de.slice(5, 7)), anoFim = Number(ate.slice(0, 4)), mesFim = Number(ate.slice(5, 7));
  const n = parseInt(iptu.parcelas, 10), parcela = num(iptu.valor_anual) / n;
  let total = 0;
  for (let p = 0; p < n; p++) {
    let mesP = parseInt(iptu.mes_inicio, 10) + p, anoP = anoIni;
    if (mesP > 12) { mesP -= 12; anoP += 1; }
    if ((anoP > anoIni || (anoP === anoIni && mesP >= mesIni)) && (anoP < anoFim || (anoP === anoFim && mesP <= mesFim))) total += parcela;
  }
  return total;
}

// ── o quadro "Resultado" ─────────────────────────────────────────────
// dados = { unidades, reservas, valorDe, lancamentos, despesas, acordos, condominios, iptus }
//   unidades: [{ id, numero, proprietario_id, proprietarios: { nome } }]
//   condominios: [{ unidade_id, valor }]  (condominio_da_unidade, 0049)
//   iptus: [{ unidade_id, valor_anual, parcelas, mes_inicio }]  (iptu_da_unidade, 0053)
// Devolve o mesmo que `_calcResultadoHolding`, com as linhas de receita
// AGRUPADAS POR ACORDO em vez de por nome: `receitas` = [{ chave, rotulo,
// tipo, badge, bruto, valor, proprietarios: [...], unidades: [...] }].
export function resultado(dados, ano, mes) {
  const periodo = periodoDe(ano, mes);
  const unidades = dados.unidades || [], acordos = dados.acordos || [];
  const valorDe = dados.valorDe || (() => null);
  const unidadePor = new Map(unidades.map(u => [u.id, u]));
  const lancs = (dados.lancamentos || []).filter(l => noPeriodo(l.data, periodo));

  // 1. bruto por unidade: reservas pro-rata + receitas avulsas de diárias
  //    (lançamento de diárias SEM reserva vinculada — no MdM, sem "[res:")
  const brutoPorUnidade = new Map();
  const mesesPorProprietario = new Map();   // p/ cota fixa: meses com atividade
  const add = (uid, v, mesRef) => {
    const u = unidadePor.get(uid); if (!u) return;
    brutoPorUnidade.set(uid, (brutoPorUnidade.get(uid) || 0) + v);
    if (mesRef) { const k = u.proprietario_id || ''; if (!mesesPorProprietario.has(k)) mesesPorProprietario.set(k, new Set()); mesesPorProprietario.get(k).add(mesRef); }
  };
  for (const r of dados.reservas || []) {
    // a reserva sobrepõe o período? (com valor 1, a fração diz se há noite dentro)
    if (receitaNoPeriodo(r, periodo, () => 1) <= 0) continue;
    // sem valor lançado conta 0 no bruto, mas ainda é "mês com atividade" da cota fixa
    add(r.unidade_id, receitaNoPeriodo(r, periodo, valorDe), r.check_in.slice(0, 7));
  }
  for (const l of lancs) {
    if (l.tipo !== 'receita' || l.categoria !== 'diarias' || l.reserva_id) continue;
    if (l.unidade_id) add(l.unidade_id, num(l.valor), l.data.slice(0, 7));
  }

  // 2. por proprietário → por acordo
  const porProprietario = new Map();
  for (const u of unidades) {
    const k = u.proprietario_id || '';
    if (!porProprietario.has(k)) porProprietario.set(k, { id: u.proprietario_id || null, nome: (u.proprietarios && u.proprietarios.nome) || 'Sem proprietário', bruto: 0, unidades: [] });
    const p = porProprietario.get(k);
    const b = brutoPorUnidade.get(u.id) || 0;
    p.bruto += b; p.unidades.push({ id: u.id, numero: u.numero, bruto: b });
  }
  const proprietarios = [...porProprietario.values()].map(p => {
    const a = p.id ? acordoDe(acordos, p.id) : null;
    // cota fixa: "R$ X × meses com atividade" — com filtro de mês, 1 se
    // houve atividade, senão 0; sem, a contagem de meses (MdM `numMeses`)
    const setM = mesesPorProprietario.get(p.id || '') || new Set();
    const meses = mes ? (setM.size > 0 ? 1 : 0) : setM.size;
    return { ...p, acordo: a, meses, valor: repasse(a, p.bruto, meses), semAcordo: !!p.id && !a };
  }).sort((x, y) => y.bruto - x.bruto);

  // as linhas do quadro: uma por acordo (próprio · cada cota fixa · percentual)
  const receitas = [];
  const proprios = proprietarios.filter(p => p.acordo && p.acordo.tipo === 'proprio');
  if (proprios.length || acordos.some(a => a.tipo === 'proprio')) receitas.push(linhaDeReceita('proprio', 'Flats próprios', proprios, '100%'));
  for (const p of proprietarios.filter(x => x.acordo && x.acordo.tipo === 'cota_fixa')) {
    receitas.push(linhaDeReceita('cota:' + p.id, 'Flats ' + p.nome, [p], { cota: num(p.acordo.valor), meses: p.meses }));
  }
  // percentuais: agrupados pelo percentual (25% de todos os terceiros numa linha, como no MdM)
  const porPct = new Map();
  for (const p of proprietarios.filter(x => x.acordo && x.acordo.tipo === 'percentual')) {
    const k = num(p.acordo.valor); if (!porPct.has(k)) porPct.set(k, []); porPct.get(k).push(p);
  }
  for (const [pct, lista] of [...porPct.entries()].sort((a, b) => b[0] - a[0])) {
    receitas.push(linhaDeReceita('pct:' + pct, lista.length === 1 ? 'Flats ' + lista[0].nome : 'Flats de terceiros (adm.)', lista, pct + '%'));
  }
  const semAcordo = proprietarios.filter(p => p.semAcordo);
  // 4. frigobar (0056): lucro líquido = receita − custo − comissão (resultado-holding.js 122–156)
  const lancsFrigobar = lancs.filter(l => l.categoria === 'frigobar');
  const recFrigobar = somar(lancsFrigobar.filter(l => l.tipo === 'receita'));
  let custoFrigobar = somar(lancsFrigobar.filter(l => l.tipo === 'despesa')), custoFonte = 'lançamentos';
  if (dados.movimentos && dados.produtos) {
    const fg = dadosDoFrigobar(dados.produtos, dados.movimentos, unidades, periodo.ini, periodo.fim);
    if (fg.totCus > 0 || fg.totRec > 0) { custoFrigobar = fg.totCus; custoFonte = 'preço de compra atual'; }
  }
  const frigobar = { ...lucroDoFrigobar(recFrigobar, custoFrigobar, dados.comissaoFrigobar), custoFonte, lancs: lancsFrigobar };
  const receitaHolding = receitas.reduce((s, x) => s + x.valor, 0) + frigobar.lucro;
  const brutoAdministrado = [...brutoPorUnidade.values()].reduce((s, v) => s + v, 0);

  // 3. despesas da imobiliária: o que ELA paga. Dos flats, só os PRÓPRIOS.
  const idsProprios = new Set(proprios.flatMap(p => p.unidades.map(u => u.id)));
  const nMeses = mes ? 1 : (ano ? 12 : 1);
  const condominioDe = new Map((dados.condominios || []).map(c => [c.unidade_id, num(c.valor)]));
  const detalheCondominio = [];
  let condLancado = 0, condPrevisto = 0;
  for (const u of unidades.filter(x => idsProprios.has(x.id))) {
    const doFlat = lancs.filter(l => l.unidade_id === u.id && l.categoria === 'condominio' && l.tipo === 'despesa');
    if (doFlat.length) { const s = somar(doFlat); condLancado += s; detalheCondominio.push({ unidade_id: u.id, numero: u.numero, valor: s, status: 'lancado' }); }
    else { const prev = (condominioDe.get(u.id) || 0) * nMeses; condPrevisto += prev; detalheCondominio.push({ unidade_id: u.id, numero: u.numero, valor: prev, status: 'previsao' }); }
  }
  const desp = fn => lancs.filter(l => l.tipo === 'despesa' && fn(l));
  const lancsLoja = desp(l => !l.unidade_id && l.categoria === 'loja');
  const lancsFunc = desp(l => !l.unidade_id && (l.categoria === 'funcionarios' || l.categoria === 'salario'));
  // IPTU: como o condomínio — lançado quando há parcela no período; senão
  // a previsão pelo cadastro (0053: as parcelas que caem no período). Sem
  // período (todos os anos) não há o que prever.
  const lancsIptu = desp(l => l.unidade_id && idsProprios.has(l.unidade_id) && l.categoria === 'iptu');
  const iptuDe = new Map((dados.iptus || []).map(i => [i.unidade_id, i]));
  const detalheIptu = [];
  let iptuLancado = 0, iptuPrevistoTotal = 0;
  for (const u of unidades.filter(x => idsProprios.has(x.id))) {
    const doFlat = lancsIptu.filter(l => l.unidade_id === u.id);
    if (doFlat.length) { const s = somar(doFlat); iptuLancado += s; detalheIptu.push({ unidade_id: u.id, numero: u.numero, valor: s, status: 'lancado' }); }
    else { const prev = iptuPrevisto(iptuDe.get(u.id), periodo.ini, periodo.fim); iptuPrevistoTotal += prev; detalheIptu.push({ unidade_id: u.id, numero: u.numero, valor: prev, status: 'previsao' }); }
  }
  const lancsOutras = desp(l => l.unidade_id && idsProprios.has(l.unidade_id) && !['condominio', 'iptu', 'frigobar', 'loja', 'funcionarios', 'salario'].includes(l.categoria || ''));
  const variaveis = (dados.despesas || []).filter(d => d.unidade_id && idsProprios.has(d.unidade_id) && noPeriodo(d.data, periodo));
  const despCondominio = condLancado + condPrevisto;
  const despLoja = somar(lancsLoja), despFunc = somar(lancsFunc), despIptu = iptuLancado + iptuPrevistoTotal, despOutras = somar(lancsOutras), despVariaveis = somar(variaveis);
  const despesaSemLoja = despCondominio + despFunc + despIptu + despVariaveis + despOutras;
  const despesaHolding = despesaSemLoja + despLoja;
  const liquidoSemLoja = receitaHolding - despesaSemLoja;
  const liquidoHolding = liquidoSemLoja - despLoja;

  return { periodo, receitas, proprietarios, semAcordo, frigobar, receitaHolding, brutoAdministrado, brutoPorUnidade,
    despCondominio, condLancado, condPrevisto, detalheCondominio, nMeses,
    iptuLancado, iptuPrevisto: iptuPrevistoTotal, detalheIptu,
    lancsLoja, lancsFunc, lancsIptu, lancsOutras, variaveis,
    despLoja, despFunc, despIptu, despOutras, despVariaveis,
    despesaSemLoja, despesaHolding, liquidoSemLoja, liquidoHolding };
}
function linhaDeReceita(chave, rotulo, lista, badge) {
  return { chave, rotulo, badge, proprietarios: lista,
    bruto: lista.reduce((s, p) => s + p.bruto, 0), valor: lista.reduce((s, p) => s + p.valor, 0),
    unidades: lista.flatMap(p => p.unidades.map(u => ({ ...u, proprietario: p.nome, valor: repasseDaUnidade(p, u) }))) };
}
// a parte de uma unidade no repasse do proprietário (cota fixa não se rateia — mostra só o bruto)
function repasseDaUnidade(p, u) {
  if (!p.acordo || p.acordo.tipo === 'cota_fixa') return null;
  return repasse(p.acordo, u.bruto, 0);
}

// ── a faixa cinza e a tabela de lançamentos (`renderFinanceiro`) ─────
// Filtra os lançamentos como o MdM: ano, mês, categoria, tipo, unidade;
// condomínio só dos flats próprios.
export function filtrarLancamentos(lancamentos, { ano, mes, categoria, tipo, unidade }, idsProprios = new Set()) {
  return (lancamentos || []).filter(l => {
    if (ano && l.data && l.data.slice(0, 4) !== ano) return false;
    if (mes && l.data && l.data.slice(5, 7) !== mes) return false;
    if (categoria && l.categoria !== categoria) return false;
    if (tipo && l.tipo !== tipo) return false;
    if (unidade && l.unidade_id !== unidade) return false;
    if (l.categoria === 'condominio' && !(l.unidade_id && idsProprios.has(l.unidade_id))) return false;
    return true;
  });
}
// "Faturamento bruto administrado" = reservas pro-rata + receitas avulsas
// (não vinculadas a reserva) no filtro; "Despesas registradas" = todo
// lançamento de despesa que passou no filtro.
export function faixaBruta(dados, filtro, r) {
  const idsProprios = new Set(r.receitas.filter(x => x.chave === 'proprio').flatMap(x => x.unidades.map(u => u.id)));
  const filtrados = filtrarLancamentos(dados.lancamentos, filtro, idsProprios);
  const avulsas = filtrados.filter(l => l.tipo === 'receita' && !l.reserva_id);
  const incluirReservas = (!filtro.categoria || filtro.categoria === 'diarias') && (!filtro.tipo || filtro.tipo === 'receita');
  let reservas = 0;
  if (incluirReservas) {
    for (const x of dados.reservas || []) {
      if (filtro.unidade && x.unidade_id !== filtro.unidade) continue;
      reservas += receitaNoPeriodo(x, r.periodo, dados.valorDe || (() => null));
    }
  }
  return { receitas: somar(avulsas) + reservas, despesas: somar(filtrados.filter(l => l.tipo === 'despesa')), filtrados };
}

// ── validação dos formulários ────────────────────────────────────────
export function validarLancamento({ data, tipo, categoria, valor, descricao }) {
  const e = [];
  if (!data) e.push('Informe a data.');
  if (!['receita', 'despesa'].includes(tipo)) e.push('Tipo inválido.');
  if (!CATEGORIAS.some(c => c.chave === categoria)) e.push('Categoria inválida.');
  if (!(num(valor) > 0)) e.push('Informe o valor.');
  if (!String(descricao || '').trim()) e.push('Informe a descrição.');  // `saveLancamentoFin`: "Preencha descricao e valor"
  return e;
}
export function validarDespesa({ data, tipo, valor }) {
  const e = [];
  if (!data) e.push('Informe a data.');
  if (!String(tipo || '').trim()) e.push('Informe o tipo da despesa.');
  if (!(num(valor) > 0)) e.push('Informe o valor.');
  return e;
}
export function validarAcordo({ tipo, valor }) {
  const e = [];
  if (!TIPOS_DE_ACORDO.some(t => t.chave === tipo)) e.push('Tipo de acordo inválido.');
  if (tipo === 'percentual' && !(num(valor) >= 0 && num(valor) <= 100)) e.push('Percentual entre 0 e 100.');
  if (tipo === 'cota_fixa' && !(num(valor) >= 0)) e.push('Cota não pode ser negativa.');
  return e;
}

// ── Despesas Variáveis (`renderDespesasOp`) ──────────────────────────
// Filtro global ano+mês; total e quantidade por unidade para a grade.
export function despesasNoFiltro(despesas, ano, mes) {
  return (despesas || []).filter(d => {
    if (!d.data) return false;
    if (ano && !d.data.startsWith(ano)) return false;
    if (mes && !d.data.startsWith(`${ano}-${mes}`)) return false;
    return true;
  });
}
export function totaisPorUnidade(despesas) {
  const m = new Map();
  for (const d of despesas) { if (!d.unidade_id) continue; const t = m.get(d.unidade_id) || { total: 0, qtd: 0 }; t.total += num(d.valor); t.qtd++; m.set(d.unidade_id, t); }
  return m;
}
// Paleta por proprietário (mesma da tela Flats do MdM, 11913)
const CORES = ['#1B3A7A', '#0f766e', '#7c3aed', '#db2777', '#f59e0b', '#dc2626', '#0ea5e9'];
export function coresPorProprietario(unidades) {
  const m = new Map();
  for (const u of unidades || []) { const k = u.proprietario_id || 'Outro'; if (!m.has(k)) m.set(k, CORES[m.size % CORES.length]); }
  return m;
}
export function ordenarPorNumero(unidades) {
  return (unidades || []).slice().sort((a, b) => (parseInt(a.numero, 10) || 0) - (parseInt(b.numero, 10) || 0));
}
